/**
 * Сервис-воркер: приложение хранится в телефоне, новая версия качается в фоне.
 *
 * Скачалась новая версия целиком — приложение говорит «Обновить» и ждёт
 * человека: перезагрузить страницу самим значило бы сбросить запись, которую
 * он, может быть, как раз набирает. Закрыл приложение и открыл снова — новая
 * версия включится и без кнопки.
 *
 * Сам воркер собирается из `sw-template.js` (см. `vite.config.ts`).
 */
type Listener = (ready: boolean) => void;

let waiting: ServiceWorker | null = null;
/** Человек нажал «Обновить»: смена версии после этого — всегда повод перезагрузиться. */
let requested = false;
const listeners = new Set<Listener>();
const notify = () => { for (const l of listeners) l(waiting !== null); };

/** Подписка на «новая версия скачана»; возвращает отписку. */
export function onUpdateReady(listener: Listener): () => void {
  listeners.add(listener);
  listener(waiting !== null);
  return () => { listeners.delete(listener); };
}

/** Сколько ждать смены версии, прежде чем перезагрузить страницу самим. */
const SWITCH_MS = 4000;

/**
 * «Обновить»: новая версия занимает место прежней, и страница перезагружается.
 *
 * Ожидающая версия берётся заново в момент нажатия. Запомненная при загрузке
 * могла устареть: после двух выкладок подряд её место заняла следующая, и
 * сигнал уходил отставленной — плашка висела на «Обновляю…» без конца
 * (03.11.2026). Смены за несколько секунд не случилось — перезагружаем сами:
 * хуже, чем зависшая кнопка, не будет ничего.
 */
export function applyUpdate(): void {
  requested = true;
  const fallback = window.setTimeout(() => location.reload(), SWITCH_MS);
  const send = (worker: ServiceWorker | null | undefined) => {
    if (!worker) { window.clearTimeout(fallback); location.reload(); return; }
    worker.postMessage({ type: 'skip-waiting' });
  };
  if (!('serviceWorker' in navigator)) { send(waiting); return; }
  void navigator.serviceWorker.getRegistration()
    .then((reg) => send(reg?.waiting ?? waiting))
    .catch(() => send(waiting));
}

export function registerServiceWorker(): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  const sw = navigator.serviceWorker;
  // Первая установка тоже берёт страницу под управление, но перезагружать
  // её незачем: она и так свежая. Перезагрузка — только после «Обновить».
  const hadController = sw.controller !== null;
  let reloading = false;
  sw.addEventListener('controllerchange', () => {
    // Страница могла открыться без воркера (на iPhone бывает) — тогда смену
    // после «Обновить» прежде пропускали, и кнопка висела на «Обновляю…».
    if ((!hadController && !requested) || reloading) return;
    reloading = true;
    location.reload();
  });

  const start = () => {
    sw.register('/sw.js').then((reg) => {
      const track = (worker: ServiceWorker | null) => {
        if (!worker) return;
        const check = () => {
          if (worker.state === 'installed' && sw.controller) { waiting = worker; notify(); }
        };
        check();
        worker.addEventListener('statechange', check);
      };
      track(reg.waiting);
      reg.addEventListener('updatefound', () => track(reg.installing));
      // Приложение на телефоне живёт днями и само страницу не перезагружает:
      // новую версию ищем, когда человек к нему возвращается, и раз в пять
      // минут — проверка спрашивает только маленький sw.js.
      // Сорвалась самая первая установка (сеть оборвалась посреди) — ставим
      // заново: без неё приложение так и не сохранится в телефоне.
      const update = () => {
        void sw.getRegistration().then((r) => (r?.active || r?.installing || r?.waiting ? r.update() : sw.register('/sw.js')))
          .catch(() => undefined);
      };
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') update(); });
      setInterval(update, 5 * 60_000);
    }).catch(() => {
      // Без воркера приложение работает как раньше — просто без памяти в телефоне.
    });
  };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}
